import Typography from '@mui/material/Typography';
import type { SearchMatch } from '@api/document/model/types';

interface SearchMatchSnippetProps {
  /** Why the row matched the search, from the API; null for a name match. */
  match?: SearchMatch | null;
  /** The search term, to highlight inside the snippet. */
  term?: string | null;
}

export interface SnippetPart {
  text: string;
  hit: boolean;
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Split `text` into runs, marking each case-insensitive occurrence of `term`. */
export function highlightParts(
  text: string,
  term?: string | null
): SnippetPart[] {
  const needle = (term ?? '').trim();
  if (!needle) return [{ text, hit: false }];
  const pattern = new RegExp(`(${escapeRegExp(needle)})`, 'gi');
  return text
    .split(pattern)
    .filter((part) => part !== '')
    .map((part) => ({
      text: part,
      hit: part.toLowerCase() === needle.toLowerCase(),
    }));
}

/** Characters of context kept before the first hit. */
const LEAD_CHARS = 30;

/**
 * Drop leading context so the first hit sits near the start of the snippet
 * (ZLAYT-1439). The server centres the hit about 80 characters in, and with
 * the file name in front and a two-line clamp the hit was cut off in the
 * table column. Cuts at a word boundary and marks the cut with "...".
 */
export function focusSnippet(text: string, term?: string | null): string {
  const needle = (term ?? '').trim().toLowerCase();
  if (!needle) return text;
  const at = text.toLowerCase().indexOf(needle);
  if (at <= LEAD_CHARS + 3) return text;
  let start = at - LEAD_CHARS;
  const space = text.indexOf(' ', start);
  if (space !== -1 && space < at) start = space + 1;
  return `...${text.slice(start)}`;
}

/**
 * The line under a My Documents result that says why it matched the search
 * (ZLAYT-1439): the file the text came from, or the generated document
 * itself, and a short snippet around the term. Renders nothing when the row
 * matched by name only.
 */
function SearchMatchSnippet({ match, term }: SearchMatchSnippetProps) {
  if (!match?.snippet) return null;
  const sourceLabel =
    match.source_type === 'file' && match.source ? match.source : 'Document';
  const snippet = focusSnippet(match.snippet, term);
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="div"
      title={`${sourceLabel}: ${match.snippet}`}
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        whiteSpace: 'normal',
        lineHeight: 1.4,
        mt: 0.25,
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{ fontWeight: 600, mr: 0.5 }}
      >
        {`${sourceLabel}:`}
      </Typography>
      {highlightParts(snippet, term).map((part, index) =>
        part.hit ? (
          <mark
            key={index}
            style={{ backgroundColor: 'rgba(255, 213, 79, 0.45)', padding: 0 }}
          >
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        )
      )}
    </Typography>
  );
}

export default SearchMatchSnippet;
